/**
 * Sequence Diagram Utilities
 *
 * Create sequence diagrams using ArchiMate elements as participants.
 * Supports both UI-based editing and PlantUML text syntax.
 */

import type { ArchiMateElement, ArchiMateDiagram } from './types';
import type { ExtendedArchiMateDiagram } from './extended-types';

// ============================================================================
// SEQUENCE DIAGRAM TYPES
// ============================================================================

export type MessageType =
  | 'sync' // Synchronous call (solid arrow)
  | 'async' // Asynchronous message (open arrow)
  | 'return' // Return message (dashed arrow)
  | 'create' // Create lifeline
  | 'destroy'; // Destroy lifeline

export type ParticipantType =
  | 'actor'
  | 'component'
  | 'service'
  | 'database'
  | 'queue'
  | 'external';

export interface Participant {
  id: string;
  elementId?: string; // Link to ArchiMate element
  name: string;
  type: ParticipantType;
  alias?: string; // Short name for PlantUML
  color?: string;
  order: number; // Left-to-right ordering
}

export interface Message {
  id: string;
  from: string; // Participant ID
  to: string; // Participant ID
  type: MessageType;
  label: string;
  description?: string;
  sequence: number; // Order in diagram
  activationStart?: boolean; // Start activation box
  activationEnd?: boolean; // End activation box
}

export interface Note {
  id: string;
  text: string;
  position: 'left' | 'right' | 'over'; // Position relative to participant
  participantId?: string; // Which participant (for left/right/over)
  participantIds?: string[]; // Multiple participants (for over)
  sequence: number; // When to show in flow
}

export interface Fragment {
  id: string;
  type: 'alt' | 'opt' | 'loop' | 'par' | 'critical' | 'break' | 'group';
  label?: string; // Condition or description
  startSequence: number;
  endSequence: number;
  children?: Fragment[]; // Nested fragments
}

export interface SequenceDiagram {
  id: string;
  name: string;
  description?: string;
  diagramId?: string; // Link to ArchiMate diagram
  participants: Participant[];
  messages: Message[];
  notes: Note[];
  fragments: Fragment[];
  createdAt: number;
  updatedAt: number;
}

// ============================================================================
// PARTICIPANT CREATION
// ============================================================================

/**
 * Create a participant from an ArchiMate element
 */
export function createParticipantFromElement(
  element: ArchiMateElement,
  order: number = 0
): Participant {
  // Map ArchiMate element types to participant types
  let participantType: ParticipantType = 'component';

  if (element.type === 'business-actor' || element.type === 'stakeholder') {
    participantType = 'actor';
  } else if (element.type.includes('service')) {
    participantType = 'service';
  } else if (element.type === 'data-object' || element.type === 'artifact') {
    participantType = 'database';
  } else if (element.type === 'business-event' || element.type === 'application-event') {
    participantType = 'queue';
  }

  return {
    id: `p-${element.id}`,
    elementId: element.id,
    name: element.name,
    type: participantType,
    alias: generateAlias(element.name),
    order,
  };
}

/**
 * Generate a short alias from a name (for PlantUML)
 */
function generateAlias(name: string): string {
  // Take first letter of each word, max 4 characters
  const words = name.split(/\s+/);
  const alias = words
    .map(w => w.charAt(0).toUpperCase())
    .join('')
    .slice(0, 4);
  return alias;
}

/**
 * Create a blank participant
 */
export function createParticipant(
  name: string,
  type: ParticipantType = 'component',
  order: number = 0
): Participant {
  return {
    id: `p-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    name,
    type,
    alias: generateAlias(name),
    order,
  };
}

// ============================================================================
// MESSAGE CREATION
// ============================================================================

/**
 * Create a message between participants
 */
export function createMessage(
  from: string,
  to: string,
  label: string,
  type: MessageType = 'sync',
  sequence: number = 0
): Message {
  return {
    id: `m-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    from,
    to,
    type,
    label,
    sequence,
  };
}

/**
 * Create a return message (convenience function)
 */
export function createReturnMessage(
  from: string,
  to: string,
  label: string = 'return',
  sequence: number = 0
): Message {
  return createMessage(from, to, label, 'return', sequence);
}

// ============================================================================
// NOTE CREATION
// ============================================================================

/**
 * Create a note
 */
export function createNote(
  text: string,
  participantId: string,
  position: 'left' | 'right' | 'over' = 'right',
  sequence: number = 0
): Note {
  return {
    id: `n-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    text,
    position,
    participantId,
    sequence,
  };
}

// ============================================================================
// FRAGMENT CREATION
// ============================================================================

/**
 * Create a fragment (alt, loop, etc.)
 */
export function createFragment(
  type: Fragment['type'],
  label: string,
  startSequence: number,
  endSequence: number
): Fragment {
  return {
    id: `f-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type,
    label,
    startSequence,
    endSequence,
  };
}

// ============================================================================
// SEQUENCE DIAGRAM CREATION
// ============================================================================

/**
 * Create a new sequence diagram
 */
export function createSequenceDiagram(
  name: string,
  diagramId?: string
): SequenceDiagram {
  return {
    id: `seq-${Date.now()}`,
    name,
    diagramId,
    participants: [],
    messages: [],
    notes: [],
    fragments: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

/**
 * Create a sequence diagram from ArchiMate elements
 */
export function createSequenceDiagramFromElements(
  name: string,
  elements: ArchiMateElement[],
  diagramId?: string
): SequenceDiagram {
  const diagram = createSequenceDiagram(name, diagramId);

  // Add participants from elements
  diagram.participants = elements.map((element, index) =>
    createParticipantFromElement(element, index)
  );

  return diagram;
}

// ============================================================================
// PLANTUML GENERATION
// ============================================================================

/**
 * Generate PlantUML syntax from sequence diagram
 */
export function generatePlantUML(diagram: SequenceDiagram): string {
  let uml = '@startuml\n';

  if (diagram.name) {
    uml += `title ${diagram.name}\n`;
  }

  uml += '\n';

  // Declare participants
  for (const participant of diagram.participants.sort((a, b) => a.order - b.order)) {
    const typeKeyword = getPlantUMLParticipantType(participant.type);
    const alias = participant.alias || participant.id;
    const color = participant.color ? ` ${participant.color}` : '';
    uml += `${typeKeyword} "${participant.name}" as ${alias}${color}\n`;
  }

  uml += '\n';

  // Generate messages and notes in sequence order
  const items = [
    ...diagram.messages.map(m => ({ type: 'message' as const, item: m, seq: m.sequence })),
    ...diagram.notes.map(n => ({ type: 'note' as const, item: n, seq: n.sequence })),
  ].sort((a, b) => a.seq - b.seq);

  const fragmentStack: Fragment[] = [];
  let currentSequence = 0;

  for (const { type, item } of items) {
    // Check if we need to close any fragments
    while (fragmentStack.length > 0 && fragmentStack[fragmentStack.length - 1].endSequence <= currentSequence) {
      const closed = fragmentStack.pop();
      uml += 'end\n';
    }

    // Check if we need to open any fragments
    for (const fragment of diagram.fragments) {
      if (fragment.startSequence === currentSequence) {
        fragmentStack.push(fragment);
        uml += `${fragment.type} ${fragment.label || ''}\n`;
      }
    }

    if (type === 'message') {
      const message = item as Message;
      const fromAlias = getParticipantAlias(diagram, message.from);
      const toAlias = getParticipantAlias(diagram, message.to);
      const arrow = getPlantUMLArrow(message.type);

      if (message.activationStart) {
        uml += `activate ${toAlias}\n`;
      }

      uml += `${fromAlias} ${arrow} ${toAlias}: ${message.label}\n`;

      if (message.activationEnd) {
        uml += `deactivate ${toAlias}\n`;
      }

      currentSequence = message.sequence;
    } else if (type === 'note') {
      const note = item as Note;
      const participantAlias = note.participantId ? getParticipantAlias(diagram, note.participantId) : '';

      if (note.position === 'over' && note.participantIds && note.participantIds.length > 1) {
        const aliases = note.participantIds.map(id => getParticipantAlias(diagram, id)).join(', ');
        uml += `note over ${aliases}\n${note.text}\nend note\n`;
      } else if (participantAlias) {
        uml += `note ${note.position} of ${participantAlias}\n${note.text}\nend note\n`;
      } else {
        uml += `note ${note.position}\n${note.text}\nend note\n`;
      }

      currentSequence = note.sequence;
    }
  }

  // Close any remaining fragments
  while (fragmentStack.length > 0) {
    fragmentStack.pop();
    uml += 'end\n';
  }

  uml += '\n@enduml';

  return uml;
}

function getPlantUMLParticipantType(type: ParticipantType): string {
  switch (type) {
    case 'actor':
      return 'actor';
    case 'database':
      return 'database';
    case 'queue':
      return 'queue';
    case 'external':
      return 'participant';
    case 'component':
    case 'service':
    default:
      return 'participant';
  }
}

function getPlantUMLArrow(type: MessageType): string {
  switch (type) {
    case 'sync':
      return '->';
    case 'async':
      return '->>';
    case 'return':
      return '-->';
    case 'create':
      return '->o';
    case 'destroy':
      return '->x';
    default:
      return '->';
  }
}

function getParticipantAlias(diagram: SequenceDiagram, participantId: string): string {
  const participant = diagram.participants.find(p => p.id === participantId);
  return participant?.alias || participantId;
}

// ============================================================================
// PLANTUML PARSING
// ============================================================================

/**
 * Parse PlantUML syntax into a sequence diagram
 * (Simplified parser - supports basic syntax)
 */
export function parsePlantUML(uml: string, diagramId?: string): SequenceDiagram {
  const lines = uml.split('\n').map(l => l.trim()).filter(l => l.length > 0);

  const diagram = createSequenceDiagram('Sequence Diagram', diagramId);
  const aliasToId = new Map<string, string>();
  let sequence = 0;

  for (const line of lines) {
    // Skip PlantUML wrapper
    if (line === '@startuml' || line === '@enduml') continue;

    // Title
    if (line.startsWith('title ')) {
      diagram.name = line.substring(6).trim();
      continue;
    }

    // Participant declaration
    const participantMatch = line.match(/^(participant|actor|database|queue)\s+"([^"]+)"\s+as\s+(\w+)/);
    if (participantMatch) {
      const [, typeStr, name, alias] = participantMatch;
      let type: ParticipantType = 'component';
      if (typeStr === 'actor') type = 'actor';
      else if (typeStr === 'database') type = 'database';
      else if (typeStr === 'queue') type = 'queue';

      const participant = createParticipant(name, type, diagram.participants.length);
      participant.alias = alias;
      diagram.participants.push(participant);
      aliasToId.set(alias, participant.id);
      continue;
    }

    // Message
    const messageMatch = line.match(/^(\w+)\s+(->|-->|->>)\s+(\w+)\s*:\s*(.+)$/);
    if (messageMatch) {
      const [, fromAlias, arrow, toAlias, label] = messageMatch;
      const fromId = aliasToId.get(fromAlias);
      const toId = aliasToId.get(toAlias);

      if (fromId && toId) {
        let msgType: MessageType = 'sync';
        if (arrow === '-->') msgType = 'return';
        else if (arrow === '->>') msgType = 'async';

        const message = createMessage(fromId, toId, label.trim(), msgType, sequence++);
        diagram.messages.push(message);
      }
      continue;
    }

    // Note
    const noteMatch = line.match(/^note\s+(left|right|over)\s+(?:of\s+)?(\w+)\s*$/);
    if (noteMatch) {
      const [, position, participantAlias] = noteMatch;
      const participantId = aliasToId.get(participantAlias);

      if (participantId) {
        // Note text is on next line(s) until "end note"
        // For simplicity, we'll just create an empty note
        const note = createNote('', participantId, position as 'left' | 'right' | 'over', sequence++);
        diagram.notes.push(note);
      }
      continue;
    }
  }

  diagram.updatedAt = Date.now();
  return diagram;
}

// ============================================================================
// VALIDATION
// ============================================================================

export interface SequenceDiagramValidation {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Validate a sequence diagram
 */
export function validateSequenceDiagram(diagram: SequenceDiagram): SequenceDiagramValidation {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check for participants
  if (diagram.participants.length === 0) {
    errors.push('Diagram must have at least one participant');
  }

  // Check for duplicate participant names
  const names = new Set<string>();
  for (const participant of diagram.participants) {
    if (names.has(participant.name)) {
      warnings.push(`Duplicate participant name: ${participant.name}`);
    }
    names.add(participant.name);
  }

  // Validate messages reference valid participants
  const participantIds = new Set(diagram.participants.map(p => p.id));
  for (const message of diagram.messages) {
    if (!participantIds.has(message.from)) {
      errors.push(`Message "${message.label}" references invalid participant: ${message.from}`);
    }
    if (!participantIds.has(message.to)) {
      errors.push(`Message "${message.label}" references invalid participant: ${message.to}`);
    }
  }

  // Check for self-calls (valid but noteworthy)
  for (const message of diagram.messages) {
    if (message.from === message.to) {
      warnings.push(`Self-call detected: ${message.label}`);
    }
  }

  // Validate fragments
  for (const fragment of diagram.fragments) {
    if (fragment.startSequence >= fragment.endSequence) {
      errors.push(`Fragment "${fragment.label}" has invalid sequence range`);
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

// ============================================================================
// EXPORT
// ============================================================================

export interface SequenceDiagramExport {
  diagram: SequenceDiagram;
  plantuml: string;
  validation: SequenceDiagramValidation;
}

/**
 * Export sequence diagram with all data
 */
export function exportSequenceDiagram(diagram: SequenceDiagram): SequenceDiagramExport {
  return {
    diagram,
    plantuml: generatePlantUML(diagram),
    validation: validateSequenceDiagram(diagram),
  };
}

// ============================================================================
// MERMAID GENERATION
// ============================================================================

/**
 * Generate Mermaid syntax from sequence diagram
 */
export function generateMermaid(diagram: SequenceDiagram): string {
  let mermaid = 'sequenceDiagram\n';

  // Add title if present
  if (diagram.name) {
    mermaid += `    title ${diagram.name}\n`;
  }

  // Declare participants in order
  for (const participant of diagram.participants.sort((a, b) => a.order - b.order)) {
    const mermaidType = getMermaidParticipantType(participant.type);
    const alias = participant.alias || sanitizeAlias(participant.name);
    mermaid += `    ${mermaidType} ${alias} as ${participant.name}\n`;
  }

  mermaid += '\n';

  // Generate messages and notes in sequence order
  const items = [
    ...diagram.messages.map(m => ({ type: 'message' as const, item: m, seq: m.sequence })),
    ...diagram.notes.map(n => ({ type: 'note' as const, item: n, seq: n.sequence })),
  ].sort((a, b) => a.seq - b.seq);

  for (const { type, item } of items) {
    if (type === 'message') {
      const message = item as Message;
      const fromAlias = getParticipantAlias(diagram, message.from);
      const toAlias = getParticipantAlias(diagram, message.to);
      const arrow = getMermaidArrow(message.type);

      if (message.activationStart) {
        mermaid += `    activate ${toAlias}\n`;
      }

      mermaid += `    ${fromAlias}${arrow}${toAlias}: ${message.label}\n`;

      if (message.activationEnd) {
        mermaid += `    deactivate ${toAlias}\n`;
      }
    } else if (type === 'note') {
      const note = item as Note;

      if (note.participantIds && note.participantIds.length > 1) {
        const firstAlias = getParticipantAlias(diagram, note.participantIds[0]);
        const lastAlias = getParticipantAlias(diagram, note.participantIds[note.participantIds.length - 1]);
        mermaid += `    Note over ${firstAlias},${lastAlias}: ${note.text}\n`;
      } else if (note.participantId) {
        const alias = getParticipantAlias(diagram, note.participantId);
        const position = note.position === 'left' ? 'left of' : 'right of';
        mermaid += `    Note ${position} ${alias}: ${note.text}\n`;
      }
    }
  }

  // Handle fragments (loops, alts, etc.)
  for (const fragment of diagram.fragments.sort((a, b) => a.startSequence - b.startSequence)) {
    const fragmentType = getMermaidFragmentType(fragment.type);
    mermaid += `    ${fragmentType} ${fragment.label || ''}\n`;
    // Note: Mermaid requires manual message placement within fragments
    // For now, we'll just add the fragment markers
    mermaid += `    end\n`;
  }

  return mermaid;
}

function getMermaidParticipantType(type: ParticipantType): string {
  switch (type) {
    case 'actor':
      return 'actor';
    case 'database':
      return 'participant'; // Mermaid doesn't have database type
    case 'queue':
      return 'participant';
    case 'external':
      return 'participant';
    case 'component':
    case 'service':
    default:
      return 'participant';
  }
}

function getMermaidArrow(type: MessageType): string {
  switch (type) {
    case 'sync':
      return '->>';
    case 'async':
      return '->>';
    case 'return':
      return '-->>';
    case 'create':
      return '->+';
    case 'destroy':
      return '->x';
    default:
      return '->>';
  }
}

function getMermaidFragmentType(type: string): string {
  switch (type) {
    case 'alt':
      return 'alt';
    case 'opt':
      return 'opt';
    case 'loop':
      return 'loop';
    case 'par':
      return 'par';
    default:
      return 'alt';
  }
}

function sanitizeAlias(name: string): string {
  // Remove special characters and spaces for Mermaid aliases
  return name.replace(/[^a-zA-Z0-9]/g, '_');
}
